import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Error from '@mui/icons-material/Error';
import { CircularProgress } from '@mui/material';

import Warning from '@mui/icons-material/Warning';
import { useAuth } from '../../../authentication/AuthenticationContext';
import { MergedAccount } from '../../../authentication/Authentication.model';
import { Modal } from '../../../_shared/modal/Modal';
import { RequestError } from '../../../Error.model';
import { myI18n } from '../../../i18n';
import { del, post } from '../../../utils/Request';
import { useSnackbar } from '../../../snackbar/SnackbarContext';
import { useConfig } from '../../../ConfigContext';

import logoSteam from '../../../_assets/steam.svg';
import logoEpic from '../../../_assets/epic.svg';
import logoStadia from '../../../_assets/stadia.svg';

interface Props {
  providerName: string;
  gameId: string;
  isDisabled: boolean;
}

export function ProviderMerge(props: Props): JSX.Element {
  const auth = useAuth();
  const { t } = useTranslation(['translation', 'error']);
  const snackbar = useSnackbar();
  const config = useConfig();
  const [showCustomErrorModal, setShowCustomErrorModal] = useState(false);
  const [mergeAccount, setMergeAccount] = useState<MergedAccount>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [previousStatus, setPreviousStatus] = useState<string>();
  const [error, setError] = useState<RequestError | null>(null);

  const getMergeAccount = (): void => {
    const provider = props.providerName === 'epic' ? 'egs' : props.providerName;
    const ma = auth.user.mergedAccounts.find((account) => account.provider === provider);
    setMergeAccount(ma);
  };

  const closeCustomErrorModal = ():void => {
    setShowCustomErrorModal(false);
  };

  const merge = (): void => {
    setIsLoading(true);
    setPreviousStatus('unmerged');
    post(`${config.clientConfig.host}/players/me/merge`, {
      data: {
        providerName: props.providerName,
        gameId: props.gameId,
      },
    }).then(() => {
      auth.getPlayer();
    }).catch((err: RequestError) => {
      setError(err);
      snackbar.addErrorMessage(err.code);
      setIsLoading(false);
    });
  };

  const unmerge = (): void => {
    setIsLoading(true);
    setPreviousStatus('merged');
    del(`${config.clientConfig.host}/players/me/unmerge/providers/${props.providerName}/games/${props.gameId}`)
      .then(() => {
        auth.getPlayer();
      })
      .catch((err: RequestError) => {
        setError(err);
        snackbar.addErrorMessage(err.code);
        setIsLoading(false);
      });
  };

  const handleShowModal = (): void => {
    if (mergeAccount?.status === 'failed') {
      setError((mergeAccount.error as RequestError));
      setShowCustomErrorModal(true);
    }
  };

  useEffect(() => {
    if (mergeAccount?.status === 'failed') {
      snackbar.addErrorMessage('account.provider.modal.descriptionGeneric', null, 'translation');
    }
  }, [mergeAccount?.status]);

  const statusRendering = (): JSX.Element => {
    let status: string;
    switch (mergeAccount?.status) {
      case 'completed':
        status = 'merged';
        if (previousStatus === 'unmerged') {
          setIsLoading(false);
          setPreviousStatus(status);
        }
        break;
      case undefined:
        status = 'unmerged';
        if (previousStatus === 'merged') {
          setIsLoading(false);
          setPreviousStatus(status);
        }
        break;
      default:
        status = mergeAccount.status;
    }

    return (
      <>
        <div
          className={`link mx-auto mt-auto ${status === 'failed' ? 'failed-btn' : ''}`}
          data-cy={`${props.providerName}-merge-status-text`}
          onClick={handleShowModal}
        >
          {t(`translation:account.my-account.${status}`)}
          {status === 'failed' ? (
            <Error fontSize='small' className='failed' />
          ) : (
            <div className={`status ${status}`} />
          )}
        </div>
        {!isLoading ? (
          <button
            type='button'
            disabled={isLoading || status === 'inProgress' || props.isDisabled}
            onClick={status === 'merged' ? unmerge : merge}
            data-cy={`${props.providerName}-${status === 'merged' ? 'unmerge' : 'merge'}-button`}
            className={`btn btn-block mx-auto mt-3 mb-0 ${status === 'merged' ? 'btn-secondary' : 'btn-primary-game'}`}
          >
            {t(`translation:account.my-account.${status === 'merged' ? 'unmerge' : 'merge'}`)}
          </button>
        ) : <div className='loader'><CircularProgress /></div>}
      </>
    );
  };

  const modalErrorRendering = (): JSX.Element => {
    if (error && myI18n.exists(`error:${error.code}`)) {
      return (
        <p>
          <span>
            {t(
              'translation:account.provider.modal.error.description1',
              { error: t(`error:${error.code}`, {
                provider: props.providerName,
              }) },
            )}
          </span>
          <br />
          <br />
          <span>
            {t('translation:account.provider.modal.error.description2')}
          </span>
        </p>
      );
    }

    return <p>{t('translation:account.provider.modal.error.descriptionGeneric')}</p>;
  };

  const providerLogo = (): string => {
    switch (props.providerName) {
      case 'steam':
        return logoSteam;
      case 'epic':
        return logoEpic;
      case 'stadia':
        return logoStadia;
      default:
        return '';
    }
  };

  useEffect(() => {
    getMergeAccount();
  }, [auth.user]);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    const getPlayerTimeout = (): void => {
      timeout = setTimeout(() => {
        auth.getPlayer();
      }, 3000);
    };

    if (mergeAccount?.taskId) {
      getPlayerTimeout();
    } else if (timeout) {
      clearTimeout(timeout);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [mergeAccount]);

  return (
    <div className='provider-merge-container text-center'>
      <img src={providerLogo()} alt={props.providerName} height='70' />
      <h4 className='mb-3'>{props.providerName.charAt(0).toUpperCase() + (props.providerName?.slice(1) || '')}</h4>

      {statusRendering()}
      {props.isDisabled && (
        <div className='warning-msg'>
          <Warning className='icon warning' />
          {t('translation:account.provider.mergeDisabledWarning')}
        </div>
      )}

      <Modal
        className='unknown-error'
        show={showCustomErrorModal}
        confirm={closeCustomErrorModal}
        textConfirm={t('translation:button.close')}
        classConfirm='btn btn-primary-game'
      >
        <h2 className='text-uppercase'>{t('translation:account.provider.modal.error.title')}</h2>
        {modalErrorRendering()}
      </Modal>
    </div>
  );
}
